import { motion } from 'framer-motion';
import { Button } from 'flowbite-react';
import {
  ArrowRightIcon,
  PhoneIcon,
  XMarkIcon,
} from '@heroicons/react/16/solid';
import {
  LinkedInIcon,
  InstagramIcon,
  FacebookIcon,
  YoutubeIcon,
} from '@/components/icons';
import { useState } from 'react';
import ContactForm from '@/components/ContactForm';

interface ContactDrawerProps {
  isOpen: boolean;
  closeDrawer: () => void;
}

interface LeftOrgDisplayProps {
  toggleMobileFormDisplay: () => void;
}

export function ContactDrawer({ isOpen, closeDrawer }: ContactDrawerProps) {
  const [isMobileFormShown, setIsMobileFormShown] = useState(false);

  const toggleMobileFormDisplay = () => {
    setIsMobileFormShown(!isMobileFormShown);
  };

  const closeAndClear = () => {
    setIsMobileFormShown(false);
    closeDrawer();
  };

  return (
    <motion.div
      animate={isOpen ? { y: 0 } : { y: '100%' }}
      className="fixed bottom-2 inline-flex items-end inset-x-0 z-[400] w-[800px] max-sm:w-screen sm:bottom-0 sm:left-0 sm:right-0 sm:top-0 max-sm:bottom-0 max-sm:left-0 max-sm:right-0 max-sm:top-0 sm:p-2"
      initial={{ y: '100%' }}
      transition={{ type: 'spring', stiffness: 100, damping: 20 }}
    >
      <div className="inline-flex gap-1 h-[600px] max-sm:max-h-[700px] py-3 max-sm:flex-col max-sm:items-end max-sm:justify-end">
        <Button
          pill
          className="w-12 h-12 max-sm:flex hidden"
          style={{ zIndex: 50 }}
          color="light"
          onClick={closeAndClear}
        >
          <XMarkIcon className="w-6 h-6" />
        </Button>

        {!isMobileFormShown && (
          <LeftOrgDisplay toggleMobileFormDisplay={toggleMobileFormDisplay} />
        )}
        <ContactForm
          isMobileFormShown={isMobileFormShown}
          inputTheme={inputTheme}
          onSuccess={closeAndClear}
        />
        <Button
          pill
          className="w-12 h-12 max-sm:hidden flex"
          color="light"
          onClick={closeDrawer}
        >
          <XMarkIcon className="w-6 h-6" />
        </Button>
      </div>
    </motion.div>
  );
}

function LeftOrgDisplay({ toggleMobileFormDisplay }: LeftOrgDisplayProps) {
  return (
    <div className="flex flex-1 flex-col">
      <div
        className="space-y-1 flex-1 flex flex-col bg-white dark:bg-zinc-900 rounded-xl overflow-hidden"
        style={{
          borderRadius: 24,
        }}
      >
        <img
          alt="Turbo Labs workspace"
          className="w-full h-1/2 object-cover flex-1"
          src="/images/conttact.png"
        />
        <div className="p-4">
          <p className="text-xl font-medium">Turbo Labs HQ</p>
          <p className="text-lg">2024 N Freedom Pl</p>
          <p className="text-lg">Fayetteville, AR, 72704</p>
          <div className="inline-flex items-center justify-end max-sm:justify-start  gap-3 mt-10 w-full">
            <a
              href="https://www.linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full bg-zinc-200 dark:bg-zinc-700 dark:text-white w-11 h-11 p-3"
            >
              <LinkedInIcon />
            </a>
            <a
              href="https://www.facebook.com"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full  bg-zinc-200 dark:bg-zinc-700  w-11 h-11 p-3 flex items-center justify-center"
            >
              <FacebookIcon />
            </a>
            <a
              href="https://www.instagram.com"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full  bg-zinc-200 dark:bg-zinc-700  w-11 h-11 p-3"
            >
              <InstagramIcon />
            </a>
            <a
              href="https://www.youtube.com"
              target="_blank"
              rel="noopener noreferrer"
              className="rounded-full  bg-zinc-200 dark:bg-zinc-700 flex items-center justify-center w-11 h-11 p-3"
            >
              <YoutubeIcon />
            </a>
          </div>
        </div>
      </div>
      <div className="items-center gap-1 hidden max-sm:flex mt-1">
        <button
          className="w-full bg-white p-4 hover:bg-zinc-100"
          style={{ borderRadius: 16 }}
          onClick={toggleMobileFormDisplay}
        >
          <div className="flex items-center justify-between w-full">
            <div className="text-lg text-zinc-600 dark:text-zinc-300">
              Send us an email
            </div>
            <ArrowRightIcon className="w-5 h-5" />
          </div>
        </button>
      </div>
      <div className="flex items-center gap-1 mt-1">
        <a href="tel:8707617555" className="flex-1">
          <button
            className="rounded-lg h-14 bg-red-500 text-white hover:bg-red-600 flex-1 justify-center inline-flex items-center w-full"
            style={{
              borderRadius: 16,
            }}
          >
            <PhoneIcon className="w-5 h-5 mr-2" />
            <div className="text-lg tracking-wider">870.761.7555</div>
          </button>
        </a>
        <button
          className="rounded-lg h-14 bg-zinc-600 flex-1 text-lg font-md text-white"
          style={{
            borderRadius: 16,
          }}
        >
          <a href="mailto:austin@turbo.inc">austin@turbo.inc</a>
        </button>
      </div>
    </div>
  );
}

// Keep your inputTheme object at the bottom since it's quite large
const inputTheme = {
  input: {
    default: {
      standard: {
        sm: 'peer block w-full appearance-none border-0 border-b-2 border-gray-300 bg-transparent px-0 py-2.5 text-xs text-gray-900 focus:border-zinc-600 focus:outline-none focus:ring-0 dark:border-zinc-600 dark:text-white dark:focus:border-zinc-300',
        md: 'peer block w-full appearance-none border-0 border-b-2 border-gray-300 bg-transparent px-0 py-2.5 text-lg text-gray-900 focus:border-zinc-600 focus:outline-none focus:ring-0 dark:border-zinc-600 dark:text-white dark:focus:border-zinc-300',
      },
    },
    success: {
      standard: {
        sm: 'peer block w-full appearance-none border-0 border-b-2 border-green-600 bg-transparent px-0 py-2.5 text-xs text-gray-900 focus:border-green-600 focus:outline-none focus:ring-0 dark:border-green-500 dark:text-white dark:focus:border-green-500',
        md: 'peer block w-full appearance-none border-0 border-b-2 border-green-600 bg-transparent px-0 py-2.5 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-0 dark:border-green-500 dark:text-white dark:focus:border-green-500',
      },
    },
    error: {
      standard: {
        sm: 'peer block w-full appearance-none border-0 border-b-2 border-red-600 bg-transparent px-0 py-2.5 text-xs text-gray-900 focus:border-red-600 focus:outline-none focus:ring-0 dark:border-red-500 dark:text-white dark:focus:border-red-500',
        md: 'peer block w-full appearance-none border-0 border-b-2 border-red-600 bg-transparent px-0 py-2.5 text-sm text-gray-900 focus:border-red-600 focus:outline-none focus:ring-0 dark:border-red-500 dark:text-white dark:focus:border-red-500',
      },
    },
  },
  label: {
    default: {
      standard: {
        sm: 'absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 text-md text-gray-500 transition-transform duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:text-zinc-600 dark:text-zinc-400 peer-focus:dark:text-zinc-500',
        md: 'absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 text-lg text-gray-500 transition-transform duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-6 peer-focus:scale-75 peer-focus:text-zinc-600 dark:text-zinc-400 peer-focus:dark:text-zinc-500',
      },
    },
    success: {
      standard: {
        sm: 'absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 text-xs text-green-600 transition-transform duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-6 peer-focus:scale-75 dark:text-green-500',
        md: 'absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 text-sm text-green-600 transition-transform duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-6 peer-focus:scale-75 dark:text-green-500',
      },
    },
    error: {
      standard: {
        sm: 'absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 text-xs text-red-600 transition-transform duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-6 peer-focus:scale-75 dark:text-red-500',
        md: 'absolute top-3 -z-10 origin-[0] -translate-y-6 scale-75 text-sm text-red-600 transition-transform duration-300 peer-placeholder-shown:translate-y-0 peer-placeholder-shown:scale-100 peer-focus:left-0 peer-focus:-translate-y-6 peer-focus:scale-75 dark:text-red-500',
      },
    },
  },
  helperText: {
    default: 'mt-2 text-xs text-gray-600 dark:text-gray-400',
    success: 'mt-2 text-xs text-green-600 dark:text-green-400',
    error: 'mt-2 text-xs text-red-600 dark:text-red-400',
  },
} as const;

export { inputTheme };
